import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Monitor, Tablet, MapPin, Clock, Shield, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

interface DeviceSession {
  id: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
  device_name: string;
  location: string;
  ip_address: string;
  last_active: string;
  is_current: boolean;
  browser: string;
  os: string;
}

const DeviceManagementSettings = () => {
  const [sessions] = useState<DeviceSession[]>([
    {
      id: '1',
      device_type: 'desktop',
      device_name: 'MacBook Pro',
      location: 'New York, NY',
      ip_address: '192.168.1.100',
      last_active: '2 minutes ago',
      is_current: true,
      browser: 'Chrome',
      os: 'macOS'
    },
    {
      id: '2',
      device_type: 'mobile',
      device_name: 'iPhone 15 Pro',
      location: 'New York, NY',
      ip_address: '192.168.1.105',
      last_active: '1 hour ago',
      is_current: false,
      browser: 'Safari',
      os: 'iOS'
    },
    {
      id: '3',
      device_type: 'tablet',
      device_name: 'iPad Air',
      location: 'Boston, MA',
      ip_address: '10.0.0.45',
      last_active: '3 days ago',
      is_current: false,
      browser: 'Safari',
      os: 'iPadOS'
    }
  ]);

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'desktop':
        return Monitor;
      case 'mobile':
        return Smartphone;
      case 'tablet':
        return Tablet;
      default:
        return Monitor;
    }
  };

  const handleRevokeSession = (sessionId: string) => {
    // TODO: Implement session revocation
  };

  const handleRevokeAll = () => {
    // TODO: Implement revoke all sessions
  };

  return (
    <div className="space-y-6">
      {/* Active Sessions Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border-blue-500/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Active Sessions
                  <Badge className="bg-blue-100 text-blue-800 text-xs">
                    {sessions.length} devices
                  </Badge>
                </CardTitle>
                <CardDescription>Manage devices with access to your account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-white/50 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Desktop</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  {sessions.filter(s => s.device_type === 'desktop').length}
                </p>
                <p className="text-xs text-muted-foreground">Active devices</p>
              </div>
              
              <div className="p-4 rounded-lg bg-white/50 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Mobile</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {sessions.filter(s => s.device_type === 'mobile').length}
                </p>
                <p className="text-xs text-muted-foreground">Active devices</p>
              </div>
              
              <div className="p-4 rounded-lg bg-white/50 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Tablet className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium">Tablet</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">
                  {sessions.filter(s => s.device_type === 'tablet').length}
                </p>
                <p className="text-xs text-muted-foreground">Active devices</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Security Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="bg-gradient-to-br from-orange-500/5 to-red-500/5 border-orange-500/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle>Security Actions</CardTitle>
                <CardDescription>Protect your account from unauthorized access</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                onClick={handleRevokeAll}
                className="flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                Revoke All Other Sessions
              </Button>
              <p className="text-sm text-muted-foreground flex items-center">
                This will sign out all devices except this one
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Device Sessions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <CardTitle>Your Devices</CardTitle>
            <CardDescription>Devices currently signed in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {sessions.map((session, index) => {
                const DeviceIcon = getDeviceIcon(session.device_type);
                
                return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-card/80 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center">
                        <DeviceIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{session.device_name}</h3>
                          {session.is_current && (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              Current Device
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {session.location}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {session.last_active}
                          </div>
                        </div>
                        
                        <div className="text-xs text-muted-foreground">
                          {session.browser} on {session.os} • {session.ip_address}
                        </div>
                      </div>
                    </div>
                    
                    {!session.is_current && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokeSession(session.id)}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        Revoke
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Login History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <CardTitle>Recent Login Activity</CardTitle>
                <CardDescription>Your recent sign-in history</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { time: '2 minutes ago', location: 'New York, NY', device: 'MacBook Pro', success: true },
                { time: '1 hour ago', location: 'New York, NY', device: 'iPhone 15 Pro', success: true },
                { time: '2 hours ago', location: 'Boston, MA', device: 'iPad Air', success: false },
                { time: '1 day ago', location: 'New York, NY', device: 'MacBook Pro', success: true },
              ].map((login, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${login.success ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div>
                      <p className="text-sm font-medium">{login.device}</p>
                      <p className="text-xs text-muted-foreground">
                        {login.location} • {login.time}
                      </p>
                    </div>
                  </div>
                  <Badge variant={login.success ? "secondary" : "destructive"} className="text-xs">
                    {login.success ? "Success" : "Failed"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DeviceManagementSettings;